<template>
  <!-- MODAL DE CONFIGURACIÓN DE HORARIOS - COMPONENTE ÚNICO -->
  <div v-if="visible" class="fixed inset-0 z-70 flex items-center justify-center">
    <!-- Background overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="handleClose"></div>
    
    <!-- Modal container -->
    <div class="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
      
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h2 class="text-lg font-semibold text-gray-800">Configuración de Horarios</h2>
        <button @click="handleClose" class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Dispositivo Header -->
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center space-x-3">
            <span class="text-sm text-gray-600">N°{{ equipo.id }}</span>
            <span class="text-base font-medium text-gray-800">{{ equipo.nombre_dispositivo || 'Dispositivo' }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <!-- Imagen del dispositivo -->
            <img :src="equipo.imagen || '/placeholder-device.png'" 
                 :alt="equipo.nombre_dispositivo" 
                 class="w-16 h-11 object-contain">
          </div>
        </div>

        <!-- Configuraciones de horario -->
        <div class="space-y-4">
          
          <!-- MAÑANA -->
          <div class="space-y-3">
            <h3 class="text-sm font-medium text-gray-700">Mañana</h3>
            <div class="flex items-center space-x-4">
              <!-- Desde -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Desde:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.manana.desde" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Hasta -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Hasta:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.manana.hasta" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Modo -->
              <div class="flex items-center space-x-2">
                <select v-model="localConfig.manana.modo" 
                        class="px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="CALOR">Calor</option>
                  <option value="FRIO">Frío</option>
                  <option value="AUTO">Auto</option>
                </select>
                <button class="w-6 h-6" :class="getIconColorClass(localConfig.manana.modo)">
                  <component :is="getIconComponent(localConfig.manana.modo)" class="w-4 h-4" />
                </button>
              </div>

              <!-- Toggle Habilitador Mañana -->
              <div class="flex flex-col items-center space-y-1">
                <span class="text-xs text-gray-500">Habilitador</span>
                <div class="relative">
                  <input type="checkbox" v-model="localConfig.manana.habilitado" class="sr-only">
                  <div :class="getToggleClasses(localConfig.manana.habilitado)" 
                       @click="localConfig.manana.habilitado = !localConfig.manana.habilitado">
                    <div :class="getToggleKnobClasses(localConfig.manana.habilitado)"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TARDE -->
          <div class="space-y-3">
            <h3 class="text-sm font-medium text-gray-700">Tarde</h3>
            <div class="flex items-center space-x-4">
              <!-- Desde -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Desde:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.tarde.desde" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Hasta -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Hasta:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.tarde.hasta" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Modo -->
              <div class="flex items-center space-x-2">
                <select v-model="localConfig.tarde.modo" 
                        class="px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="CALOR">Calor</option>
                  <option value="FRIO">Frío</option>
                  <option value="AUTO">Auto</option>
                </select>
                <button class="w-6 h-6" :class="getIconColorClass(localConfig.tarde.modo)">
                  <component :is="getIconComponent(localConfig.tarde.modo)" class="w-4 h-4" />
                </button>
              </div>

              <!-- Toggle Habilitador Tarde -->
              <div class="flex flex-col items-center space-y-1">
                <span class="text-xs text-gray-500">Habilitador</span>
                <div class="relative">
                  <input type="checkbox" v-model="localConfig.tarde.habilitado" class="sr-only">
                  <div :class="getToggleClasses(localConfig.tarde.habilitado)" 
                       @click="localConfig.tarde.habilitado = !localConfig.tarde.habilitado">
                    <div :class="getToggleKnobClasses(localConfig.tarde.habilitado)"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- NOCHE -->
          <div class="space-y-3">
            <h3 class="text-sm font-medium text-gray-700">Noche</h3>
            <div class="flex items-center space-x-4">
              <!-- Desde -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Desde:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.noche.desde" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Hasta -->
              <div class="flex items-center space-x-2">
                <label class="text-sm text-gray-600">Hasta:</label>
                <div class="flex items-center space-x-1">
                  <input type="time" v-model="localConfig.noche.hasta" 
                         class="px-2 py-1 border border-gray-300 rounded text-sm w-20">
                  <button class="w-6 h-6 text-blue-500 hover:text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Modo -->
              <div class="flex items-center space-x-2">
                <select v-model="localConfig.noche.modo" 
                        class="px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="CALOR">Calor</option>
                  <option value="FRIO">Frío</option>
                  <option value="AUTO">Auto</option>
                </select>
                <button class="w-6 h-6" :class="getIconColorClass(localConfig.noche.modo)">
                  <component :is="getIconComponent(localConfig.noche.modo)" class="w-4 h-4" />
                </button>
              </div>

              <!-- Toggle Habilitador Noche -->
              <div class="flex flex-col items-center space-y-1">
                <span class="text-xs text-gray-500">Habilitador</span>
                <div class="relative">
                  <input type="checkbox" v-model="localConfig.noche.habilitado" class="sr-only">
                  <div :class="getToggleClasses(localConfig.noche.habilitado)" 
                       @click="localConfig.noche.habilitado = !localConfig.noche.habilitado">
                    <div :class="getToggleKnobClasses(localConfig.noche.habilitado)"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <button @click="ajustarTodos" class="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors">
          Ajustar Todos
        </button>
        <button @click="guardarHorarios" class="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
          Guardar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModalConfiguracionHorarios',

  // PROPS QUE RECIBE DESDE EL COMPONENTE PADRE
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    equipo: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        nombre_dispositivo: '',
        imagen: null
      })
    },
    configuracionInicial: {
      type: Object,
      default: () => ({
        habilitadorPrincipal: true,
        manana: {
          desde: '07:00',
          hasta: '11:30',
          modo: 'CALOR',
          habilitado: true
        },
        tarde: {
          desde: '14:00',
          hasta: '21:10',
          modo: 'FRIO',
          habilitado: true
        },
        noche: {
          desde: '00:00',
          hasta: '04:50',
          modo: 'CALOR',
          habilitado: false
        }
      })
    }
  },

  // EVENTOS QUE EMITE AL COMPONENTE PADRE
  emits: ['close', 'save', 'update:visible'],

  data() {
    return {
      // COPIA LOCAL DE LA CONFIGURACIÓN PARA EVITAR MUTACIONES DIRECTAS
      localConfig: {}
    }
  },

  // COMPONENTES SVG LOCALES
  components: {
    IconoCalor: {
      template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd"></path>
        </svg>
      `
    },
    
    IconoFrio: {
      template: `
        <svg fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l-1 1v3l-3-3-1.5 1.5 3 3H6l-1 1 1 1h3.5l-3 3L8 14l3-3v3.5l1 1 1-1V11l3 3 1.5-1.5-3-3H18l1-1-1-1h-3.5l3-3L16 4l-3 3V3.5z"/>
        </svg>
      `
    },
    
    IconoAuto: {
      template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"></path>
        </svg>
      `
    }
  },

  // WATCHERS PARA SINCRONIZAR PROPS CON DATA LOCAL
  watch: {
    configuracionInicial: {
      handler(newVal) {
        this.localConfig = JSON.parse(JSON.stringify(newVal))
      },
      immediate: true,
      deep: true
    },
    
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    }
  },

  // LIMPIAR OVERFLOW AL DESTRUIR COMPONENTE
  beforeUnmount() {
    document.body.style.overflow = 'auto'
  },

  methods: {
    // MANEJAR CIERRE DEL MODAL
    handleClose() {
      this.$emit('close')
      this.$emit('update:visible', false)
    },

    // APLICAR HORARIO DE MAÑANA A TODOS LOS PERÍODOS
    ajustarTodos() {
      const horarioBase = { ...this.localConfig.manana }
      this.localConfig.tarde = { ...horarioBase }
      this.localConfig.noche = { ...horarioBase }
      
      console.log('Aplicando horario de mañana a todos los períodos')
    },

    // GUARDAR CONFIGURACIÓN Y EMITIR AL PADRE
    guardarHorarios() {
      const configuracionFinal = {
        equipoId: this.equipo.id,
        configuracion: { ...this.localConfig }
      }
      
      this.$emit('save', configuracionFinal)
      console.log('Guardando configuración:', configuracionFinal)
      this.handleClose()
    },

    // MÉTODOS HELPER PARA CLASES DINÁMICAS
    getIconColorClass(modo) {
      const colores = {
        'CALOR': 'text-orange-500',
        'FRIO': 'text-blue-500',
        'AUTO': 'text-green-500'
      }
      return colores[modo] || 'text-gray-500'
    },

    getIconComponent(modo) {
      const iconos = {
        'CALOR': 'IconoCalor',
        'FRIO': 'IconoFrio',
        'AUTO': 'IconoAuto'
      }
      return iconos[modo] || 'IconoAuto'
    },

    getToggleClasses(habilitado) {
      return [
        'w-10 h-6 rounded-full shadow-inner cursor-pointer transition-colors',
        habilitado ? 'bg-blue-500' : 'bg-gray-300'
      ]
    },

    getToggleKnobClasses(habilitado) {
      return [
        'w-4 h-4 bg-white rounded-full shadow transform translate-y-1 transition-transform',
        habilitado ? 'translate-x-5' : 'translate-x-1'
      ]
    }
  }
}
</script>